import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { ComparisonPeriod, useAppSelector, useComparisonPeriod, useWeekRange, WeekRange } from 'src/utils/Hooks';
import { useBaseTopicRequestBuilder } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/utils/useBaseTopicRequestBuilder';
import { TopicRequestBuilder } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/utils/TopicRequestBuilder';
import {
  getFirstWeekIdOfYear,
  getNextYearWeekId,
  getPreviousWeekId,
  getPreviousYearWeekId,
  weekIdToTimestamp,
  yearPartOfWeekId
} from 'src/utils/dateUtils';
import { calculatePercentChange } from 'src/utils/app';
import { TopicCardRecord, TopicPillFilter, TopicType } from '../types';
import {
  topicTrafficServerProxy,
  GetTopicSearchResponseRecord
} from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/serverProxy';
import { topicSettingsServerProxy } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/serverProxy';
import { userMetadataServerProxy } from 'src/serverProxy/userMetadataServerProxy';
import _isNil from 'lodash/isNil';
import { getTopicOrTopicGroupType } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/utils';
import { TOPIC_GROUPS_PER_CARD } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/TopicGroupSettingsPage';
import { TopicGroup } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/types';
import { useMemo } from 'react';
import { TOPIC_QUERY_KEYS } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/topicQueryKeys';

// Type guards
function isTopicRecord(
  record: GetTopicSearchResponseRecord | GetTopicGroupSummaryPageMetricsResponseRecord
): record is GetTopicSearchResponseRecord {
  return 'topicId' in record && !('topicGroupId' in record);
}

function isTopicGroupRecord(
  record: GetTopicSearchResponseRecord | GetTopicGroupSummaryPageMetricsResponseRecord
): record is GetTopicGroupSummaryPageMetricsResponseRecord {
  return 'topicGroupId' in record;
}

interface GetTopicGroupSummaryPageMetricsResponseRecord {
  topicGroupId: string;
  topicGroupName: string;
  topicId: string;
  topicName: string;
  totalTraffic: number;
  organicTraffic: number;
  paidTraffic: number;
}

interface GetTopicMetricsByWeekResponseRecord {
  weekId: number;
  topicId: string;
  topicName: string;
  totalTraffic: number;
  organicTraffic: number;
  paidTraffic: number;
}

interface GetTopicMetricsByWeekResponse {
  records: GetTopicMetricsByWeekResponseRecord[];
}

export interface GetTopicGroupSummaryPageMetricsResponse {
  records: GetTopicGroupSummaryPageMetricsResponseRecord[];
}

function getPaginationIndices(pageNumber: number, pageSize: number) {
  const startIndex = (pageNumber - 1) * pageSize;
  const endIndex = pageNumber * pageSize;
  return { startIndex, endIndex };
}

export const useComparisonWeeksForSpline = () => {
  const weekRange = useWeekRange();
  const comparisonPeriod = useComparisonPeriod();
  const defaultStartWeekId = useAppSelector((state) => state.mainTimePeriod.startWeek);
  const defaultEndWeekId = useAppSelector((state) => state.mainTimePeriod.endWeek);
  // Special case, when comparing last week with prior year we want
  // to show the graph for YTD but the legend should just be last week
  // when calculating forecast total, we should not bring this condition(forecast does not have last week condition - and it bypasses all forecastArgs)
  const isLastWeekWithPriorYear = useMemo(() => {
    return weekRange === WeekRange.LAST_WEEK && comparisonPeriod === ComparisonPeriod.PRIOR_YEAR;
  }, [weekRange, comparisonPeriod]);

  const defaultComparisonStartWeekId = useAppSelector((state) => state.comparisonTimePeriod.startWeek);
  const defaultComparisonEndWeekId = useAppSelector((state) => state.comparisonTimePeriod.endWeek);

  const startWeekId = useMemo(() => {
    return isLastWeekWithPriorYear ? getFirstWeekIdOfYear(yearPartOfWeekId(defaultEndWeekId)) : defaultStartWeekId;
  }, [defaultEndWeekId, defaultStartWeekId, isLastWeekWithPriorYear]);

  const comparisonStartWeekId = useMemo(
    () => (isLastWeekWithPriorYear ? getPreviousYearWeekId(startWeekId) : defaultComparisonStartWeekId),
    [defaultComparisonStartWeekId, isLastWeekWithPriorYear, startWeekId]
  );

  const comparisonEndWeekId = useMemo(
    () =>
      isLastWeekWithPriorYear || (weekRange === WeekRange.YTD && comparisonPeriod === ComparisonPeriod.PRIOR_YEAR)
        ? getPreviousWeekId(startWeekId)
        : defaultComparisonEndWeekId,
    [comparisonPeriod, defaultComparisonEndWeekId, isLastWeekWithPriorYear, startWeekId, weekRange]
  );

  return {
    comparisonStartWeek: comparisonStartWeekId,
    comparisonEndWeek: comparisonEndWeekId
  };
};

/**
 * Gets all custom, owned, and standard topics and topic groups
 */
export function useTopics({
  topicFilter,
  topicType,
  searchInput = '',
  page,
  itemsPerPage
}: {
  topicFilter?: TopicPillFilter;
  topicType?: TopicType;
  searchInput?: string;
  page?: number;
  itemsPerPage?: number;
} = {}) {
  const retailerId = useAppSelector((state) => state.retailer.id);
  const { startWeek, endWeek } = useAppSelector((state) => state.mainTimePeriod);
  const comparisonPeriod = useComparisonPeriod();
  const weekRange = useWeekRange();
  const { comparisonStartWeek, comparisonEndWeek } = useComparisonWeeksForSpline();

  const isLastWeekPriorYear = weekRange === WeekRange.LAST_WEEK && comparisonPeriod === ComparisonPeriod.PRIOR_YEAR;
  const isYTDPriorYear = weekRange === WeekRange.YTD && comparisonPeriod === ComparisonPeriod.PRIOR_YEAR;

  // For Last Week vs Prior Year, we want to show YTD line but focus on last week
  const effectiveStartWeek = isLastWeekPriorYear ? getFirstWeekIdOfYear(yearPartOfWeekId(endWeek)) : startWeek;
  
  // Handle all Prior Year comparison cases
  const shouldUsePreviousYearWeeks = useMemo(() => {
    if (comparisonPeriod !== ComparisonPeriod.PRIOR_YEAR) {
      return false;
    }
    return isYTDPriorYear || isLastWeekPriorYear;
  }, [comparisonPeriod, isYTDPriorYear, isLastWeekPriorYear]);

  const baseTopicSearchRequest = useBaseTopicRequestBuilder();

  const topicsSearchRequest = new TopicRequestBuilder(baseTopicSearchRequest.build())
    .setStartWeekId(effectiveStartWeek)
    .setEndWeekId(endWeek)
    .setSortField('totalTraffic')
    .setResultCount(500) // TODO use server pagination
    .setTopicType(topicType === TopicType.All ? undefined : topicType)
    .setRetailerId(+retailerId)
    .setTopicNameFuzzyFilter(searchInput)
    .build();

  const topicsSearchComparisonRequest = new TopicRequestBuilder(topicsSearchRequest)
    .setStartWeekId(shouldUsePreviousYearWeeks ? getPreviousYearWeekId(effectiveStartWeek) : comparisonStartWeek)
    .setEndWeekId(shouldUsePreviousYearWeeks ? getPreviousYearWeekId(endWeek) : comparisonEndWeek)
    .build();

  const topicGroupsSearchRequest = new TopicRequestBuilder(baseTopicSearchRequest.build())
    .setStartWeekId(effectiveStartWeek)
    .setEndWeekId(endWeek)
    .setSortField('totalTraffic')
    .setResultCount(500) // TODO use server pagination
    .setRetailerId(+retailerId)
    .setTopicGroupNameFuzzyFilter(searchInput)
    .setTopicType(topicType === TopicType.All ? undefined : topicType)
    .build();

  const topicGroupsSearchComparisonRequest = new TopicRequestBuilder(topicGroupsSearchRequest)
    .setStartWeekId(shouldUsePreviousYearWeeks ? getPreviousYearWeekId(effectiveStartWeek) : comparisonStartWeek)
    .setEndWeekId(shouldUsePreviousYearWeeks ? getPreviousYearWeekId(endWeek) : comparisonEndWeek)
    .build();

  // We don't need to fetch topic groups if we're only fetching topics
  const includeTopics = [TopicPillFilter.All, TopicPillFilter.Topics].includes(topicFilter);

  // We don't need to fetch topics if we're only fetching topic groups
  const includeTopicGroups = [TopicPillFilter.All, TopicPillFilter.TopicGroups].includes(topicFilter);

  const { data, isLoading } = useQuery({
    queryKey: [
      TOPIC_QUERY_KEYS.all,
      topicsSearchRequest,
      topicsSearchComparisonRequest,
      topicFilter,
      page,
      comparisonPeriod,
      weekRange
    ],
    queryFn: async () => {
      const [
        topicsResponse,
        topicsComparisonResponse,
        allTopicsResponse,
        { data: topicGroupsResponse },
        { data: topicGroupsComparisonResponse },
        { data: allTopicGroupsResponse }
      ] = await Promise.all([
        includeTopics
          ? topicTrafficServerProxy.getTopicSearchMetrics(topicsSearchRequest)
          : Promise.resolve({ records: [] }),
        includeTopics
          ? topicTrafficServerProxy.getTopicSearchMetrics(topicsSearchComparisonRequest)
          : Promise.resolve({ records: [] }),
        includeTopics
          ? topicTrafficServerProxy.getAllTopicSearchMetrics(topicsSearchComparisonRequest)
          : Promise.resolve({ records: [] }),
        includeTopicGroups
          ? axios.post<GetTopicGroupSummaryPageMetricsResponse>(
              '/api/beacontopics/GetTopicGroupSummaryPageMetrics',
              topicGroupsSearchRequest
            )
          : Promise.resolve({ data: { records: [] } }),
        includeTopicGroups
          ? axios.post<GetTopicGroupSummaryPageMetricsResponse>(
              '/api/beacontopics/GetTopicGroupSummaryPageMetrics',
              topicGroupsSearchComparisonRequest
            )
          : Promise.resolve({ data: { records: [] } }),
        includeTopicGroups
          ? axios.post<GetTopicGroupSummaryPageMetricsResponse>(
              '/api/beacontopics/GetAllTopicGroupSummaryPageMetrics',
              topicGroupsSearchComparisonRequest
            )
          : Promise.resolve({ data: { records: [] } })
      ]);

      // Get the indices for pagination. We don't want to fetch all metadata/weekly data if we're not going to display it right away.
      const { startIndex, endIndex } = getPaginationIndices(page, itemsPerPage);

      // Build the comparison maps
      const comparisonTopicIdMap = new Map<string, GetTopicSearchResponseRecord>();
      const comparisonTopicGroupIdMap = new Map<string, GetTopicGroupSummaryPageMetricsResponseRecord>();
      
      topicsComparisonResponse.records.filter(isTopicRecord).forEach(record => {
        comparisonTopicIdMap.set(record.topicId, record);
      });

      topicGroupsComparisonResponse.records.filter(isTopicGroupRecord).forEach(record => {
        comparisonTopicGroupIdMap.set(record.topicGroupId, record);
      });

      // Then build the current period maps
      const allTopics = new Map<string, GetTopicSearchResponseRecord>();
      const allTopicGroups = new Map<string, GetTopicGroupSummaryPageMetricsResponseRecord>();
      
      // Add current period topics
      topicsResponse.records.filter(isTopicRecord).forEach(record => {
        allTopics.set(record.topicId, record);
      });
      
      // Add comparison period topics if they don't exist in current period
      comparisonTopicIdMap.forEach((record, topicId) => {
        if (!allTopics.has(topicId)) {
          allTopics.set(topicId, {
            ...record,
            totalTraffic: 0,  // Set current period traffic to 0 since it doesn't exist
            organicTraffic: 0,
            paidTraffic: 0
          });
        }
      });

      // Add current period topic groups
      topicGroupsResponse.records.filter(isTopicGroupRecord).forEach(record => {
        allTopicGroups.set(record.topicGroupId, record);
      });
      
      // Add comparison period topic groups if they don't exist in current period
      comparisonTopicGroupIdMap.forEach((record, topicGroupId) => {
        if (!allTopicGroups.has(topicGroupId)) {
          allTopicGroups.set(topicGroupId, {
            ...record,
            totalTraffic: 0,  // Set current period traffic to 0 since it doesn't exist
            organicTraffic: 0,
            paidTraffic: 0
          });
        }
      });

      const mergedRecords = [
        ...topicsResponse?.records.map(r => ({ ...r, source: 'topicsResponse' })) || [],
        ...topicGroupsResponse?.records.map(r => ({ ...r, source: 'topicGroupsResponse' })) || [],
        ...topicsComparisonResponse?.records.map(r => ({ ...r, source: 'topicsComparisonResponse' })) || [],
        ...topicGroupsComparisonResponse?.records.map(r => ({ ...r, source: 'topicGroupsComparisonResponse' })) || [],
        ...allTopicsResponse?.records.map(r => ({ ...r, source: 'allTopicsResponse' })) || [],
        ...allTopicGroupsResponse?.records.map(r => ({ ...r, source: 'allTopicGroupsResponse' })) || []
      ] as (
        | GetTopicSearchResponseRecord
        | GetTopicGroupSummaryPageMetricsResponseRecord
      )[];
      
      // Use a Map to track unique topic/topic group IDs while preserving priority
      const uniqueRecords = new Map<string, GetTopicSearchResponseRecord | GetTopicGroupSummaryPageMetricsResponseRecord>();

      mergedRecords.forEach(record => {
        const id = (record as any).topicId || (record as any).topicGroupId;
        if (!uniqueRecords.has(id)) {
          uniqueRecords.set(id, record);
        }
      });
      
      const sortedTopicAndGroups = [...uniqueRecords.values()]
        .sort((a, b) => (b.totalTraffic || 0) - (a.totalTraffic || 0))
        .slice(startIndex, endIndex);
      
      const paginatedTopicIds = [...new Set(sortedTopicAndGroups.filter(isTopicRecord).map((record) => record.topicId))];

      const paginatedTopicGroupIds = [
        ...new Set(sortedTopicAndGroups.filter(isTopicGroupRecord).map((record) => record.topicGroupId))
      ];

      const allTopicIds = [...new Set([...uniqueRecords.values()]
        .filter(isTopicRecord)
        .map((record) => record.topicId))];

      const allTopicGroupIds = [
        ...new Set([...uniqueRecords.values()]
          .filter(isTopicGroupRecord)
          .map((record) => record.topicGroupId))
      ];

      // Fetch weekly data for all topics and topic groups from both periods
      const [topicsByWeek, comparisonTopicsByWeek, topicGroupsByWeek, comparisonTopicGroupsByWeek] = await Promise.all([
        // Fetch weekly data if there are topics in either period
        paginatedTopicIds.length > 0
          ? topicSettingsServerProxy.getTopicMetricsByWeek(
              new TopicRequestBuilder(baseTopicSearchRequest.build())
                .setStartWeekId(effectiveStartWeek)
                .setEndWeekId(endWeek)
                .setTopicIds(paginatedTopicIds)
                .setSortField('weekId')
                .build()
            )
          : Promise.resolve({ records: [] }),
        paginatedTopicIds.length > 0
          ? topicSettingsServerProxy.getTopicMetricsByWeek(
              new TopicRequestBuilder(baseTopicSearchRequest.build())
                .setStartWeekId(comparisonStartWeek)
                .setEndWeekId(comparisonEndWeek)
                .setTopicIds(paginatedTopicIds)
                .setSortField('weekId')
                .build()
            )
          : Promise.resolve({ records: [] }),
        paginatedTopicGroupIds.length > 0
          ? topicSettingsServerProxy.getTopicGroupMetricsByWeek(
              new TopicRequestBuilder(baseTopicSearchRequest.build())
                .setStartWeekId(effectiveStartWeek)
                .setEndWeekId(endWeek)
                .setTopicGroupIds(paginatedTopicGroupIds)
                .setSortField('weekId')
                .build()
            )
          : Promise.resolve({ records: [] }),
        paginatedTopicGroupIds.length > 0
          ? topicSettingsServerProxy.getTopicGroupMetricsByWeek(
              new TopicRequestBuilder(baseTopicSearchRequest.build())
                .setStartWeekId(comparisonStartWeek)
                .setEndWeekId(comparisonEndWeek)
                .setTopicGroupIds(paginatedTopicGroupIds)
                .setSortField('weekId')
                .build()
            )
          : Promise.resolve({ records: [] })
      ]);

      // Get metadata for all topics and topic groups
      const [topicIdToMetadata, topicGroupIdToMetadata, topicIdToTopicGroupsMap] = await Promise.all([
        topicSettingsServerProxy.bulkGetTopics(paginatedTopicIds),
        topicSettingsServerProxy.bulkGetTopicGroups(paginatedTopicGroupIds),
        topicSettingsServerProxy
          .getTopicGroupsContainingTopics({
            retailerId: +retailerId,
            topicIds: paginatedTopicIds
          })
          .then(async (topicsWithGroups) => {
            const groupIds = topicsWithGroups.records.reduce((acc, record) => {
              record.topicGroupIds.slice(0, TOPIC_GROUPS_PER_CARD).forEach((topicGroupId) => {
                acc.add(topicGroupId);
              });
              return acc;
            }, new Set<string>());

            const topicGroupsMap = await topicSettingsServerProxy.bulkGetTopicGroups([...groupIds]);

            return topicsWithGroups.records.reduce((acc, record) => {
              acc[record.topicId] = record.topicGroupIds
                .slice(0, TOPIC_GROUPS_PER_CARD)
                .map((topicGroupId) => topicGroupsMap[topicGroupId]);
              return acc;
            }, {} as Record<string, TopicGroup[]>);
          })
      ]);

      async function getUserMetadata() {
        const userIds = [
          ...new Set(
            [...Object.values(topicIdToMetadata), ...Object.values(topicGroupIdToMetadata)].map(
              (topic) => topic.ownedByUserId
            )
          )
        ].filter((item) => !_isNil(item));

        if (userIds.length < 1) {
          return {};
        }

        const userMetadata = await userMetadataServerProxy.getUserProfiles(userIds);
        return userMetadata;
      }
      // Get User Metadata
      const userMetadata = await getUserMetadata();

      // Map topics with their data
      const topics: TopicCardRecord[] = allTopicIds.map((topicId) => {
        const record = allTopics.get(topicId) ?? {
          topicId,
          topicName: topicIdToMetadata[topicId]?.topicName ?? '',
          totalTraffic: 0,
          organicTraffic: 0,
          paidTraffic: 0
        };

        const comparisonRecord = comparisonTopicIdMap.get(topicId);
        const splineDataPoints = topicsByWeek.records
          .filter((weekRecord) => weekRecord.topicId === topicId)
          .map((weekRecord) => [weekIdToTimestamp(weekRecord.weekId), weekRecord.totalTraffic]);

        // If no weekly data exists, create a flat zero line
        const splineData = splineDataPoints.length === 0 
          ? Array.from({ length: endWeek - effectiveStartWeek + 1 }, (_, i) => {
              const currentWeek = effectiveStartWeek + i;
              return [weekIdToTimestamp(currentWeek), 0];
            }).sort((a, b) => a[0] - b[0])
          : splineDataPoints;

        const comparisonSplineDataPoints = comparisonTopicsByWeek.records
          .filter((weekRecord) => weekRecord.topicId === topicId)
          .map((weekRecord) => {
            const adjustedWeekId = comparisonPeriod === ComparisonPeriod.PRIOR_YEAR
              ? weekIdToTimestamp(getNextYearWeekId(weekRecord.weekId))
              : weekIdToTimestamp(weekRecord.weekId);
            return [adjustedWeekId, weekRecord.totalTraffic];
          });

        // If no comparison weekly data exists, create a flat zero line
        const comparisonSplineData = comparisonSplineDataPoints.length === 0
          ? Array.from({ length: comparisonEndWeek - comparisonStartWeek + 1 }, (_, i) => {
              const currentWeek = comparisonStartWeek + i;
              const adjustedWeekId = comparisonPeriod === ComparisonPeriod.PRIOR_YEAR
                ? weekIdToTimestamp(getNextYearWeekId(currentWeek))
                : weekIdToTimestamp(currentWeek);
              return [adjustedWeekId, 0];
            }).sort((a, b) => a[0] - b[0])
          : comparisonSplineDataPoints;

        const finalSplineData = comparisonPeriod === ComparisonPeriod.PRIOR_PERIOD && comparisonSplineData.length > 0
          ? [comparisonSplineData[comparisonSplineData.length - 1], ...splineData]
          : splineData;

        const topicMetadata = topicIdToMetadata[topicId] ?? null;
        const owner = userMetadata[topicMetadata?.ownedByUserId] ?? null;

        const totalTraffic = isLastWeekPriorYear
          ? topicsByWeek.records
              .filter(weekRecord => weekRecord.topicId === topicId && weekRecord.weekId === endWeek)
              .reduce((sum, weekRecord) => sum + weekRecord.totalTraffic, 0)
          : record.totalTraffic ?? 0;

        const comparisonTraffic = isLastWeekPriorYear
          ? comparisonTopicsByWeek.records
              .filter(weekRecord => weekRecord.topicId === topicId && weekRecord.weekId === getPreviousYearWeekId(endWeek))
              .reduce((sum, weekRecord) => sum + weekRecord.totalTraffic, 0)
          : comparisonRecord?.totalTraffic ?? 0;

        return {
          id: topicId,
          owner,
          topicMetadata,
          topicGroups: topicIdToTopicGroupsMap[topicId] ?? [],
          ownership: getTopicOrTopicGroupType(topicMetadata?.sharedWithEntityId, topicMetadata?.ownedByUserId),
          name: record.topicName ?? topicMetadata?.topicName ?? '',
          type: 'topic' as const,
          splineData: finalSplineData,
          comparisonSplineData,
          totalTraffic,
          totalTrafficPercentChange: calculatePercentChange(totalTraffic, comparisonTraffic)
        };
      });

      // Map topic groups with their data
      const topicGroups: TopicCardRecord[] = allTopicGroupIds.map((topicGroupId) => {
        const record = allTopicGroups.get(topicGroupId) ?? {
          topicGroupId,
          topicGroupName: topicGroupIdToMetadata[topicGroupId]?.topicGroupName ?? '',
          totalTraffic: 0,
          organicTraffic: 0,
          paidTraffic: 0
        };

        const comparisonRecord = comparisonTopicGroupIdMap.get(topicGroupId);
        const splineDataPoints = topicGroupsByWeek.records
          .filter((weekRecord) => weekRecord.topicGroupId === topicGroupId)
          .map((weekRecord) => [weekIdToTimestamp(weekRecord.weekId), weekRecord.totalTraffic])
          .sort((a, b) => a[0] - b[0]);

        // If no weekly data exists, create a flat zero line
        const splineData = splineDataPoints.length === 0
          ? Array.from({ length: endWeek - effectiveStartWeek + 1 }, (_, i) => {
              const currentWeek = effectiveStartWeek + i;
              return [weekIdToTimestamp(currentWeek), 0];
            }).sort((a, b) => a[0] - b[0])
          : splineDataPoints;

        const comparisonSplineDataPoints = comparisonTopicGroupsByWeek.records
          .filter((weekRecord) => weekRecord.topicGroupId === topicGroupId)
          .map((weekRecord) => {
            const adjustedWeekId = comparisonPeriod === ComparisonPeriod.PRIOR_YEAR
              ? weekIdToTimestamp(getNextYearWeekId(weekRecord.weekId))
              : weekIdToTimestamp(weekRecord.weekId);
            return [adjustedWeekId, weekRecord.totalTraffic];
          })
          .sort((a, b) => a[0] - b[0]);

        // If no comparison weekly data exists, create a flat zero line
        const comparisonSplineData = comparisonSplineDataPoints.length === 0
          ? Array.from({ length: comparisonEndWeek - comparisonStartWeek + 1 }, (_, i) => {
              const currentWeek = comparisonStartWeek + i;
              const adjustedWeekId = comparisonPeriod === ComparisonPeriod.PRIOR_YEAR
                ? weekIdToTimestamp(getNextYearWeekId(currentWeek))
                : weekIdToTimestamp(currentWeek);
              return [adjustedWeekId, 0];
            }).sort((a, b) => a[0] - b[0])
          : comparisonSplineDataPoints;

        const finalSplineData = comparisonPeriod === ComparisonPeriod.PRIOR_PERIOD && comparisonSplineData.length > 0
          ? [comparisonSplineData[comparisonSplineData.length - 1], ...splineData]
          : splineData;

        const topicMetadata = topicGroupIdToMetadata[topicGroupId] ?? null;
        const owner = userMetadata[topicMetadata?.ownedByUserId] ?? null;

        const totalTraffic = isLastWeekPriorYear
          ? topicGroupsByWeek.records
              .filter(weekRecord => weekRecord.topicGroupId === topicGroupId && weekRecord.weekId === endWeek)
              .reduce((sum, weekRecord) => sum + weekRecord.totalTraffic, 0)
          : record.totalTraffic ?? 0;

        const comparisonTraffic = isLastWeekPriorYear
          ? comparisonTopicGroupsByWeek.records
              .filter(weekRecord => weekRecord.topicGroupId === topicGroupId && weekRecord.weekId === getPreviousYearWeekId(endWeek))
              .reduce((sum, weekRecord) => sum + weekRecord.totalTraffic, 0)
          : comparisonRecord?.totalTraffic ?? 0;

        return {
          id: topicGroupId,
          owner,
          topicMetadata,
          ownership: getTopicOrTopicGroupType(topicMetadata?.sharedWithEntityId, topicMetadata?.ownedByUserId),
          name: record.topicGroupName ?? topicMetadata?.topicGroupName ?? '',
          type: 'topicGroup' as const,
          splineData: finalSplineData,
          comparisonSplineData,
          totalTraffic,
          totalTrafficPercentChange: calculatePercentChange(totalTraffic, comparisonTraffic),
          topicGroups: []  // Topic groups don't have nested topic groups
        };
      });

      // Sort all records by total traffic
      return [...topics, ...topicGroups].sort((a, b) => b.totalTraffic - a.totalTraffic)
    }
  });

  return { topics: data ?? [], isLoading };
}
